import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import RemoveScheduleConfirmationModal from "@/components/global/Chat/components/ScheduleModal/RemoveScheduleConfirmationModal";
import {
  CancelSchedule,
  Check,
  Download,
  ExpandView,
  Report,
  Schedule,
  ScheduleEdit
} from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import Tabs from "@/components/controls/Tabs";
import Badge from "@/components/ui/badge";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { buildScheduledForText } from "@/components/global/Chat/components/ScheduleModal/buildScheduleSummary";
import { ReportingScheduler } from "@/entities/reportingScheduler/types";

interface DreamerAttachmentMessageCardProps {
  title: string;
  viewTabOptions: Record<string, JSX.Element>;
  viewMode: string;
  onViewModeChange: (mode: string) => void;
  disabledOptions?: string[];
  tabsDataTest?: string;
  onExpand: () => void;
  expandTestId?: string;
  expandAriaLabel?: string;
  onDownload?: () => void;
  isDownloading?: boolean;
  canDownload?: boolean;
  downloadAriaLabel?: string;
  onSchedule?: () => void;
  onCancelSchedule?: () => void;
  onManageSchedules?: () => void;
  isSchedulable?: boolean;
  scheduleData?: ReportingScheduler | null;
  /** Replaces the schedule button and badge when the answer can be saved. */
  saveControl?: React.ReactNode;
  modal?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const DreamerAttachmentMessageCard: React.FC<
  DreamerAttachmentMessageCardProps
> = ({
  title,
  viewTabOptions,
  viewMode,
  onViewModeChange,
  disabledOptions,
  tabsDataTest,
  onExpand,
  expandTestId,
  expandAriaLabel,
  onDownload,
  isDownloading = false,
  canDownload = true,
  downloadAriaLabel,
  onSchedule,
  onCancelSchedule,
  onManageSchedules,
  isSchedulable = false,
  scheduleData,
  saveControl,
  modal,
  footer,
  children
}) => {
  const { t } = useTranslation();
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);

  const scheduledMenuOptions: DetailedViewOptions = {
    editSchedule: {
      action: () => onSchedule?.(),
      customIcon: (
        <IconContainer size="5" color="secondary">
          <ScheduleEdit />
        </IconContainer>
      )
    },
    cancelSchedule: {
      action: () => setIsRemoveConfirmOpen(true),
      customIcon: (
        <IconContainer size="5" color="secondary">
          <CancelSchedule />
        </IconContainer>
      )
    },
    manageSchedules: {
      action: () => onManageSchedules?.(),
      customIcon: (
        <IconContainer size="5" color="secondary">
          <Report />
        </IconContainer>
      )
    }
  };

  // A saved report's row has no cadence, and is not a schedule to show.
  const scheduleConfig = scheduleData?.scheduleConfig;
  const scheduledTooltipText = scheduleConfig
    ? buildScheduledForText(t, scheduleConfig)
    : undefined;

  return (
    <div className="flex flex-col gap-2 py-1.5 px-4 items-start">
      <div className="w-full bg-surface-elevation2 border-[0.5px] border-border-widget-card rounded-2xl shadow-md flex flex-col overflow-hidden">
        <div className="flex w-full justify-between items-center gap-2 px-4 py-4">
          <Text as="div" variant="lgLabel">
            {title}
          </Text>
          <div className="flex items-center gap-2">
            <Tabs
              size="sm"
              options={viewTabOptions}
              value={viewMode}
              valueSelected={onViewModeChange}
              disabledOptions={disabledOptions}
              dataTest={tabsDataTest}
            />
            <IconButton
              size="lg"
              className="p-[5.5px]"
              onClick={onExpand}
              data-test={expandTestId}
              aria-label={expandAriaLabel}
            >
              <ExpandView />
            </IconButton>
          </div>
        </div>
        {children}
        {footer}
      </div>
      <div className="flex gap-2 items-center">
        <IconButton
          size="lg"
          className="p-[5.5px]"
          onClick={onDownload}
          disabled={!canDownload || isDownloading}
          aria-label={downloadAriaLabel}
        >
          <Download />
        </IconButton>
        {saveControl}
        {!saveControl &&
          onSchedule &&
          (scheduleConfig || isSchedulable) &&
          (scheduleConfig ? (
            <OptionsDropdownMenu
              tPrefix="chat.scheduleModal"
              options={scheduledMenuOptions}
              align="start"
              tooltipText={scheduledTooltipText}
              disableCloseAutoFocus
              customTrigger={
                <span tabIndex={0} className="inline-flex size-fit">
                  <Badge
                    aria-label={t("chat.scheduledAriaLabel")}
                    data-test="scheduled-badge"
                    className="border-blue-700 h-8 bg-[rgba(var(--surface-chips-natural-rgb),0.2)] cursor-pointer hover:shadow-[inset_0_0_0_9999px_var(--white-8)] data-[state=open]:shadow-[inset_0_0_0_9999px_var(--white-8)]"
                  >
                    <IconContainer className="size-[18px]">
                      <Check />
                    </IconContainer>
                    {t("chat.scheduled")}
                  </Badge>
                </span>
              }
            />
          ) : (
            <Button
              variant="secondary"
              size="lg"
              onClick={onSchedule}
              aria-label={t("chat.scheduleAriaLabel")}
              data-test="schedule-button"
              className="h-8"
            >
              <IconContainer size="sm">
                <Schedule />
              </IconContainer>
              {t("chat.schedule")}
            </Button>
          ))}
      </div>
      {modal}
      <RemoveScheduleConfirmationModal
        isOpen={isRemoveConfirmOpen}
        setIsOpen={setIsRemoveConfirmOpen}
        onConfirm={() => onCancelSchedule?.()}
      />
    </div>
  );
};

export default DreamerAttachmentMessageCard;
